/**
 * Shared image staging picker — preview + drag-drop dropzone.
 *
 * Shows a current photo (from a URL) or a staged replacement (a File object),
 * and lets the user pick a new image via drag-drop or file browse. Staging
 * only — the chosen File is handed up via `onSelect`; the caller is
 * responsible for uploading on save. Calling `onSelect(null)` clears the
 * staged file; if the caller also needs to delete a server-side photo it
 * should respond to that in its own handler.
 *
 * Contains:
 * - ImagePicker — preview tile + dropzone (default export)
 */

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Button, Stack, Typography, alpha } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { formatBytes } from '@sophocles/utils/formatUtils';

const PREVIEW_SIZE = 120;

/**
 * @param {object} props
 * @param {string|null} [props.currentUrl] - URL of the currently saved image
 * @param {File|null} [props.file] - staged File object (not yet uploaded)
 * @param {function(File|null): void} props.onSelect - called with the chosen
 *   File, or null when the user clears the staged selection
 */
function ImagePicker({ currentUrl, file, onSelect }) {
  // Object URLs must be revoked or they leak; track the active one and
  // release it whenever the staged file changes or the picker unmounts.
  const [stagedUrl, setStagedUrl] = useState(null);
  useEffect(() => {
    if (!file) {
      setStagedUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setStagedUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openPicker,
  } = useDropzone({
    onDrop: accepted => {
      if (accepted?.[0]) onSelect(accepted[0]);
    },
    accept: { 'image/*': [], 'image/heic': ['.heic'], 'image/heif': ['.heif'] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const previewUrl = stagedUrl || currentUrl || null;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
      <Avatar
        variant="rounded"
        src={previewUrl || undefined}
        sx={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE, bgcolor: 'grey.100', color: 'text.disabled' }}
      >
        <AddPhotoAlternateOutlinedIcon fontSize="large" />
      </Avatar>

      <Box
        {...getRootProps()}
        sx={{
          flex: 1,
          borderRadius: 'var(--radius-md)',
          border: 2,
          borderStyle: 'dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: theme => (isDragActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent'),
          px: 3,
          py: 2.5,
          textAlign: 'center',
          transition:
            'border-color var(--duration-normal) var(--easing-standard), background-color var(--duration-normal) var(--easing-standard)',
        }}
      >
        <input {...getInputProps()} />
        {file ? (
          <Stack spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatBytes(file.size)} — staged, not yet saved
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="text" onClick={openPicker}>
                Replace
              </Button>
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={<CloseIcon fontSize="small" />}
                onClick={() => onSelect(null)}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1} alignItems="center">
            <Typography variant="subtitle2">
              {isDragActive ? 'Drop image to upload' : 'Drag and drop an image, or browse'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentUrl ? 'Choosing a new image replaces the current photo.' : 'No photo set yet.'}
            </Typography>
            <Button size="small" variant="outlined" onClick={openPicker}>
              Browse
            </Button>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

ImagePicker.propTypes = {
  currentUrl: PropTypes.string,
  file: PropTypes.instanceOf(File),
  onSelect: PropTypes.func.isRequired,
};

export default ImagePicker;
